// src/data/store.js
import { supabase } from '../supabaseClient';

function normalizeDate(dateStr) {
  if (!dateStr) return dateStr;
  const s = String(dateStr).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.substring(0, 10).split('-');
    return d + '/' + m + '/' + y;
  }
  return s;
}

export const getEmployees = async () => {
  const { data, error } = await supabase.from('employees').select('*').order('id');
  if (error) throw error;
  return data || [];
};
export const saveEmployee = async (emp) => {
  const { data, error } = await supabase.from('employees').upsert(emp).select();
  if (error) throw error;
  return data?.[0];
};
export const deleteEmployee = async (id) => {
  const { error } = await supabase.from('employees').delete().eq('id', id);
  if (error) throw error;
};
export const getPosts = async () => {
  const { data, error } = await supabase.from('posts').select('*').order('id');
  if (error) throw error;
  return (data || []).map(p => ({ ...p, guardsPerShift: p.guards_per_shift ?? p.guardsPerShift ?? 1, radioMetros: p.radio_metros ?? p.radioMetros ?? 100 }));
};
export const savePost = async (post) => {
  const dbPost = {
    name: post.name,
    location: post.location || null,
    address: post.address || null,
    lat: post.lat === '' || post.lat === undefined ? null : post.lat,
    lng: post.lng === '' || post.lng === undefined ? null : post.lng,
    radio_metros: post.radioMetros || post.radio_metros || 100,
    armed: post.armed ?? false,
    notes: post.notes || null,
    shifts: post.shifts || [],
    coverage: post.coverage || '24/7',
    days: post.days || [0,1,2,3,4,5,6],
    guards_per_shift: post.guardsPerShift || post.guards_per_shift || 1,
    required_guards: post.guardsPerShift || post.guards_per_shift || 1
  };
  let data, error;
  if (post.id) {
    const result = await supabase.from('posts').update(dbPost).eq('id', post.id).select();
    data = result.data; error = result.error;
  } else {
    const result = await supabase.from('posts').insert(dbPost).select();
    data = result.data; error = result.error;
  }
  if (error) throw error;
  return data?.[0];
};
export const deletePost = async (id) => {
  const { error } = await supabase.from('posts').delete().eq('id', id);
  if (error) throw error;
};
export const getSchedules = async () => {
  let allData = [];
  let from = 0;
  const pageSize = 1000;

  while (true) {
    const { data, error } = await supabase
      .from('schedules')
      .select('*')
      .order('date')
      .range(from, from + pageSize - 1);

    if (error) throw error;
    if (!data || data.length === 0) break;

    allData = allData.concat(data);

    if (data.length < pageSize) break;
    from += pageSize;
  }

  return allData.map(s => ({
    ...s,
    date: normalizeDate(s.date),
    employeeId: s.employee_id || s.employeeId,
    postId: s.post_id || s.postId,
    isVacancy: s.is_vacancy || s.isVacancy || false,
    status: s.status || 'confirmed',
  }));
};
function mapSchedule(s) {
  return {
    ...s,
    date: normalizeDate(s.date),
    employeeId: s.employee_id || s.employeeId,
    postId: s.post_id || s.postId,
    isVacancy: s.is_vacancy || s.isVacancy || false,
    status: s.status || 'confirmed',
  };
}

// Todas las formas de texto en que puede estar guardada una fecha
// (dd/mm/yyyy, mm/dd/yyyy y yyyy-mm-dd) para cada dia entre desde y hasta (Date).
function fechasCandidatas(desde, hasta) {
  const set = new Set();
  const d = new Date(desde.getFullYear(), desde.getMonth(), desde.getDate());
  const fin = new Date(hasta.getFullYear(), hasta.getMonth(), hasta.getDate());
  while (d <= fin) {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = String(d.getFullYear());
    set.add(dd + '/' + mm + '/' + yyyy);
    set.add(mm + '/' + dd + '/' + yyyy);
    set.add(yyyy + '-' + mm + '-' + dd);
    d.setDate(d.getDate() + 1);
  }
  return Array.from(set);
}

// Trae SOLO los turnos entre dos fechas (Date) en vez de toda la tabla.
export const getSchedulesRange = async (desde, hasta) => {
  const fechas = fechasCandidatas(desde, hasta);
  let allData = [];
  // Bloques de 60 fechas para no generar URLs demasiado largas
  for (let i = 0; i < fechas.length; i += 60) {
    const bloque = fechas.slice(i, i + 60);
    let from = 0;
    const pageSize = 1000;
    while (true) {
      const { data, error } = await supabase
        .from('schedules')
        .select('*')
        .in('date', bloque)
        .order('date')
        .range(from, from + pageSize - 1);
      if (error) throw error;
      if (!data || data.length === 0) break;
      allData = allData.concat(data);
      if (data.length < pageSize) break;
      from += pageSize;
    }
  }
  return allData.map(mapSchedule);
};

// Historial maximo que cargan las pantallas (Dashboard, Vacantes, Documentos, Horarios)
export const MESES_HISTORIAL = 3;
// Bisemanas hacia atras permitidas en Horarios/Documentos (~3 meses)
export const MIN_BISEMANA = -6;

// Turnos de los ultimos 3 meses + los proximos 60 dias (no toda la tabla)
export const getSchedulesRecent = async () => {
  const hoy = new Date();
  const desde = new Date(hoy); desde.setMonth(hoy.getMonth() - MESES_HISTORIAL);
  const hasta = new Date(hoy); hasta.setDate(hoy.getDate() + 60);
  return getSchedulesRange(desde, hasta);
};

export const saveSchedule = async (sched) => {
  const dbSched = { employee_id: sched.employee_id || sched.employeeId || null, employee_name: sched.employee_name || sched.employeeName || null, post_id: sched.post_id || sched.postId || null, post_name: sched.post_name || sched.postName || null, date: normalizeDate(sched.date), day_of_week: sched.day_of_week || sched.dayOfWeek || null, shift: sched.shift || null, hours: sched.hours || null, is_overtime: sched.is_overtime || sched.isOvertime || false, is_vacancy: sched.is_vacancy || sched.isVacancy || false, is_sunday: sched.is_sunday || sched.isSunday || false, week_num: sched.week_num || sched.weekNum || 0, status: sched.status || 'confirmed' };
  if (sched.id) dbSched.id = sched.id;
  const { data, error } = await supabase.from('schedules').upsert(dbSched).select();
  if (error) throw error;
  return data?.[0];
};
export const saveSchedules = async (schedules) => {
  const dbScheds = schedules.map(sched => {
    const s = { employee_id: sched.employee_id || sched.employeeId || null, employee_name: sched.employee_name || sched.employeeName || null, post_id: sched.post_id || sched.postId || null, post_name: sched.post_name || sched.postName || null, date: normalizeDate(sched.date), day_of_week: sched.day_of_week || sched.dayOfWeek || null, shift: sched.shift || null, hours: sched.hours || null, is_overtime: sched.is_overtime || sched.isOvertime || false, is_vacancy: sched.is_vacancy || sched.isVacancy || false, is_sunday: sched.is_sunday || sched.isSunday || false, week_num: sched.week_num || sched.weekNum || 0, status: sched.status || 'confirmed' };
    if (sched.id) s.id = sched.id;
    return s;
  });
  const { data, error } = await supabase.from('schedules').upsert(dbScheds).select();
  if (error) throw error;
  return data || [];
};
export const deleteSchedule = async (id) => {
  const { error } = await supabase.from('schedules').delete().eq('id', id);
  if (error) throw error;
};

// Helper: parse date string dd/mm/yyyy or yyyy-mm-dd to Date object
function parseDateToObj(dateStr) {
  if (!dateStr) return null;
  const s = String(dateStr).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [d, m, y] = s.split('/');
    return new Date(Number(y), Number(m) - 1, Number(d));
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.substring(0, 10).split('-');
    return new Date(Number(y), Number(m) - 1, Number(d));
  }
  return null;
}

export const getAppointments = async () => {
  const { data, error } = await supabase.from('appointments').select('*').order('date');
  if (error) throw error;
  const now = new Date();
  const filtered = (data || []).filter(a => {
    // Auto-hide appointments older than 30 days past end date (or start date if no end)
                                       const endDate = a.date_end ? parseDateToObj(a.date_end) : parseDateToObj(a.date);
    if (!endDate) return true;
    const daysDiff = Math.floor((now - endDate) / (1000 * 60 * 60 * 24));
    return daysDiff <= 30;
  });
  return filtered.map(a => ({
    ...a,
    date: normalizeDate(a.date),
    dateEnd: a.date_end ? normalizeDate(a.date_end) : null,
    employeeId: a.employee_id || a.employeeId
  }));
};
export const addAppointment = async (appt) => {
  const dbAppt = {
    employee_id: appt.employee_id || appt.employeeId,
    date: normalizeDate(appt.date),
    date_end: appt.dateEnd || appt.date_end ? normalizeDate(appt.dateEnd || appt.date_end) : null,
    type: appt.type || 'Medica',
    notes: appt.notes || null
  };
  const { data, error } = await supabase.from('appointments').upsert(dbAppt).select();
  if (error) throw error;
  return data?.[0];
};
export const updateAppointment = async (id, appt) => {
  const dbAppt = {
    employee_id: appt.employee_id || appt.employeeId,
    date: normalizeDate(appt.date),
    date_end: appt.dateEnd || appt.date_end ? normalizeDate(appt.dateEnd || appt.date_end) : null,
    type: appt.type || 'Medica',
    notes: appt.notes || null
  };
  const { error } = await supabase.from('appointments').update(dbAppt).eq('id', id);
  if (error) throw error;
};
export const saveAppointment = addAppointment;
export const deleteAppointment = async (id) => {
  const { error } = await supabase.from('appointments').delete().eq('id', id);
  if (error) throw error;
};

// APPROVAL REQUESTS - Supabase para visibilidad multi-dispositivo
export const getApprovalRequests = async () => {
  const { data, error } = await supabase.from('approval_requests').select('*').order('created_at', { ascending: false });
  if (error) { console.error('getApprovalRequests error:', error); return []; }
  return data || [];
};
export const getApprovalRequestsList = getApprovalRequests;
export const addApprovalRequest = async (req) => {
  const { data, error } = await supabase.from('approval_requests').insert({
    type: req.type || 'edit_schedule',
    action: req.action || req.type,
    requested_by: req.requestedBy || req.requested_by || 'Desconocido',
    employee_name: req.employeeName || req.employee_name || null,
    date: req.date || null,
    shift: req.shift || null,
    notes: req.notes || null,
    status: 'pending',
  }).select();
  if (error) { console.error('addApprovalRequest error:', error); throw error; }
  return data?.[0];
};
export const updateApprovalRequest = async (id, status, resolvedBy) => {
  const { error } = await supabase.from('approval_requests').update({ status, resolved_at: new Date().toISOString(), resolved_by: resolvedBy || '' }).eq('id', id);
  if (error) throw error;
};

export const initializeStorage = async () => { return true; };
